import { useEffect, useState, useMemo, useCallback } from "react";
import { Redirect, useHistory } from "react-router";
import { useLocation, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { getMonthsAgo } from "../../components/formatted/FormattedDateAndTime";
import ForbiddenModule from "../../components/ForbiddenModule";
import PageHeader from "../../components/PageHeader";
import CampaignStatusBadge from "./components/CampaignStatusBadge";
import CampaignDateBadge from "./components/CampaignDateBadge";
import CampaignStatusAlert from "./components/CampaignStatusAlert";
import CampaignSuccessBanner from "./components/CampaignSuccessBanner";
import { useCampaignData } from "./hooks/useCampaignData";
import { useCampaignComments } from "./hooks/useCampaignComments";
import {
  getCampaignStatus,
  getContainerInfos,
  getCampaignZoneDisplay,
} from "./hooks/useCampaignListData";
import CampaignZoneCell from "./components/CampaignZoneCell";
import CampaignAnalysis from "./components/CampaignAnalysis";
import CampaignNotificationHistoryTab from "./components/CampaignNotificationHistoryTab";
import CampaignScheduledEmptyState from "./components/CampaignScheduledEmptyState";
import CampaignCancelModal from "./components/CampaignCancelModal";
import { computeCampaignImprovementPercent } from "./utils/computeCampaignImprovementPercent";

const Campaign = () => {
  const location = useLocation();
  const history = useHistory();
  const { id: campaignIdFromRoute } = useParams();
  const campaignFromState = location.state?.campaign;
  const { t, i18n } = useTranslation();
  const corporationModules = useMemo(() => {
    const stored = localStorage.getItem("corporationModules");
    return stored ? JSON.parse(stored) : null;
  }, []);
  const [campaignModuleAllowed, setCampaignModuleAllowed] = useState(true);

  const initialPeriodStart = useMemo(() => {
    if (campaignFromState?.start) {
      const start = new Date(campaignFromState.start);
      start.setMonth(start.getMonth() - 1);
      return start.toISOString();
    }
    return getMonthsAgo(new Date(), 3).toISOString();
  }, [campaignFromState]);

  const initialPeriodStop = useMemo(() => {
    if (campaignFromState?.stop) {
      const stop = new Date(campaignFromState.stop);
      stop.setMonth(stop.getMonth() + 1);
      return stop.toISOString();
    }
    return null;
  }, [campaignFromState]);

  const [periodStart] = useState(initialPeriodStart);
  const [periodStop] = useState(initialPeriodStop);
  const [selectedCampaign, setSelectedCampaign] = useState(
    campaignIdFromRoute ? -1 : 0,
  );
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [activeTab, setActiveTab] = useState("analyse");
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const viewMode = Cookies.get("viewMode") || "client";
  const isAkanthasAccount = (Cookies.get("userEmail") || "").includes(
    "@akanthas.com",
  );

  const { campaignPeriods, isLoadingCampaignPeriods } = useCampaignData(
    periodStart,
    periodStop,
  );

  const { comments, isLoading } = useCampaignComments(
    campaignPeriods,
    selectedCampaign,
    i18n,
    activeTab === "analyse",
  );

  const token = Cookies.get("userToken");

  const currentCampaign = campaignPeriods[selectedCampaign];

  const headerData = useMemo(() => {
    const source = campaignFromState || currentCampaign;
    if (!source) {
      return {
        addressName: "-",
        status: null,
        containerInfos: [],
        zoneLabel: "",
        flowLabel: "",
        start: null,
        stop: null,
        progressionPercent: null,
      };
    }

    const zoneDisplay = getCampaignZoneDisplay(source);

    return {
      addressName: source.addressName || source.address?.name || "-",
      status: source.status || getCampaignStatus(source),
      containerInfos: source.containerInfos || getContainerInfos(source),
      zoneLabel: source.zoneLabel || zoneDisplay.zoneLabel,
      flowLabel: source.flowLabel || zoneDisplay.flowLabel,
      start: source.start,
      stop: source.stop,
      progressionPercent:
        source.progressionPercent ?? source.treatmentPercent ?? null,
    };
  }, [campaignFromState, currentCampaign]);

  const campaignPeriod = currentCampaign || campaignFromState || null;

  const improvementPercent = useMemo(
    () => computeCampaignImprovementPercent(comments, campaignPeriod),
    [comments, campaignPeriod],
  );

  const titleBadges = (
    <>
      {headerData.status ? (
        <CampaignStatusBadge status={headerData.status} />
      ) : null}
      <CampaignDateBadge start={headerData.start} stop={headerData.stop} />
    </>
  );

  const subtitleText =
    headerData.zoneLabel || headerData.flowLabel ? (
      <CampaignZoneCell
        zoneLabel={headerData.zoneLabel}
        flowLabel={headerData.flowLabel}
      />
    ) : null;

  useEffect(() => {
    if (corporationModules !== null) {
      setCampaignModuleAllowed(corporationModules?.showCampaignModule || false);
    }
  }, [corporationModules]);

  useEffect(() => {
    if (campaignIdFromRoute && campaignPeriods.length > 0 && !hasAutoSelected) {
      const index = campaignPeriods.findIndex(
        (c) => c._id?.toString() === campaignIdFromRoute,
      );
      setSelectedCampaign(index >= 0 ? index : 0);
      setHasAutoSelected(true);
    }
  }, [campaignIdFromRoute, campaignPeriods, hasAutoSelected]);

  const isScheduledCampaign = headerData.status === "Scheduled";

  const cancelCampaignButton = isScheduledCampaign ? (
    <button
      type="button"
      className="modal-button modal-button-cancel whitespace-nowrap"
      onClick={() => setIsCancelModalOpen(true)}
    >
      {t("campaign.cancelCampaign")}
    </button>
  ) : null;

  const handleCampaignCancelled = useCallback(() => {
    history.push("/campaign");
  }, [history]);

  if (!token) {
    return <Redirect to={{ pathname: "/login" }} />;
  }

  return (
    <div className="settings-page-wrapper settings-page-wrapper--campaign-detail">
      <PageHeader
        showBackButton
        backRoute="/campaign"
        backLabel={t("Back to campaigns")}
        titleContent={
          <div className="flex items-center gap-3 flex-wrap">
            <span>{headerData.addressName}</span>
            {titleBadges}
          </div>
        }
        titleContentMain={<span>{headerData.addressName}</span>}
        titleContentBadges={titleBadges}
        subtitle={subtitleText}
        tabs={[
          { id: "analyse", label: t("Analyse") },
          {
            id: "historique",
            label: t("campaign.notificationHistoryTab"),
          },
        ]}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId)}
        actions={cancelCampaignButton}
      />
      <div className="settings-content-wrapper settings-content-wrapper--campaign-detail">
        {!campaignModuleAllowed &&
        !(isAkanthasAccount && viewMode === "admin") &&
        location.pathname.includes("campaign") ? (
          <ForbiddenModule viewType="campaign" />
        ) : (
          <div className="campaign-detail-shell">
            {activeTab === "analyse" &&
            campaignPeriod &&
            headerData.status !== "Scheduled" ? (
              <div className="campaign-detail-banners">
                <CampaignStatusAlert campaignPeriod={campaignPeriod} />
                {headerData.status === "Completed" ? (
                  <CampaignSuccessBanner
                    improvementPercent={improvementPercent}
                  />
                ) : null}
              </div>
            ) : null}

            {activeTab === "historique" && campaignPeriod ? (
              headerData.status === "Scheduled" ? (
                <CampaignScheduledEmptyState campaignPeriod={campaignPeriod} />
              ) : (
                <CampaignNotificationHistoryTab
                  campaignPeriod={campaignPeriod}
                />
              )
            ) : null}

            {activeTab === "analyse" && (
              <CampaignAnalysis
                comments={comments}
                campaignPeriods={campaignPeriods}
                selectedCampaign={selectedCampaign}
                isLoading={isLoading}
                isLoadingCampaignPeriods={isLoadingCampaignPeriods}
              />
            )}
          </div>
        )}
      </div>
      {campaignPeriod ? (
        <CampaignCancelModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          campaign={campaignPeriod}
          onCancelled={handleCampaignCancelled}
        />
      ) : null}
    </div>
  );
};

export default Campaign;
